import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/lib/api";
import { Trash2, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="cart-page-empty">
        <ShoppingBag size={48} className="mx-auto text-black/30 mb-6" />
        <h1 className="font-display text-5xl tracking-tighter mb-4">SIGN IN TO VIEW CART</h1>
        <Link to="/login" className="inline-block bg-black text-white px-8 py-4 font-display tracking-widest text-sm hover-invert border border-black">LOGIN</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={48} className="mx-auto text-black/30 mb-6" />
        <h1 className="font-display text-5xl tracking-tighter mb-4">YOUR CART IS EMPTY</h1>
        <p className="text-black/50 mb-8">Find something worth wearing.</p>
        <Link to="/shop/all" className="inline-block bg-black text-white px-8 py-4 font-display tracking-widest text-sm hover-invert border border-black">SHOP NOW</Link>
      </div>
    );
  }

  const shipping = subtotal >= 1499 ? 0 : 99;
  const total = subtotal + shipping;

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12" data-testid="cart-page">
      <h1 className="font-display text-6xl tracking-tighter mb-10">BAG ({items.length})</h1>

      <div className="grid lg:grid-cols-[1fr_400px] gap-10">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={`${it.product_id}-${it.size}-${it.color}`} className="grid grid-cols-[120px_1fr_auto] gap-4 border border-black/10 p-4" data-testid={`cart-item-${it.product_id}`}>
              <Link to={`/p/${it.product_id}`}>
                <img src={it.product?.images?.[0]} alt={it.product?.name} className="w-full aspect-[3/4] object-cover" />
              </Link>
              <div className="space-y-1">
                <Link to={`/p/${it.product_id}`} className="font-display text-lg tracking-tight uppercase">{it.product?.name}</Link>
                <div className="text-xs font-mono text-black/40">SIZE {it.size} · {it.color}</div>
                <div className="font-display text-base mt-2">{formatINR(it.product?.price)}</div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-black/30">
                    <button onClick={() => updateQty(it, Math.max(0, it.qty - 1))} className="w-8 h-8 hover-invert" data-testid={`cart-minus-${it.product_id}`}>−</button>
                    <span className="w-8 text-center text-sm font-display">{it.qty}</span>
                    <button onClick={() => updateQty(it, it.qty + 1)} className="w-8 h-8 hover-invert" data-testid={`cart-plus-${it.product_id}`}>+</button>
                  </div>
                  <button onClick={() => removeItem(it)} className="text-black/60 hover:text-[#FF3B30] inline-flex items-center gap-1 text-xs font-display tracking-widest" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash2 size={14} /> REMOVE
                  </button>
                </div>
              </div>
              <div className="text-right font-display text-lg">{formatINR((it.product?.price || 0) * it.qty)}</div>
            </div>
          ))}
        </div>

        <div className="border border-black/15 p-6 lg:sticky lg:top-32 lg:self-start h-fit">
          <h2 className="font-display text-2xl tracking-tighter mb-6">ORDER SUMMARY</h2>
          <div className="space-y-3 text-sm">
            <Row l="SUBTOTAL" v={formatINR(subtotal)} />
            <Row l="SHIPPING" v={shipping === 0 ? "FREE" : formatINR(shipping)} />
            <div className="border-t border-black/15 pt-3 mt-3">
              <Row l="TOTAL" v={formatINR(total)} big />
            </div>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="w-full mt-6 bg-[#FF3B30] text-white py-4 font-display tracking-widest text-sm hover:bg-black inline-flex items-center justify-center gap-2"
            data-testid="checkout-btn"
          >
            CHECKOUT <ArrowRight size={16} />
          </button>
          <p className="text-xs text-black/40 mt-4 font-mono text-center">SECURE CHECKOUT · COD AVAILABLE</p>
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, big }) {
  return (
    <div className={`flex justify-between ${big ? "font-display text-xl" : ""}`}>
      <span className={big ? "" : "text-black/60"}>{l}</span>
      <span>{v}</span>
    </div>
  );
}
